import { useState, useEffect, useCallback } from 'react';
import { getAssignmentById, isLoggedIn } from '../../../service';

export const hasInstructionContent = (html) => {
  if (!html) return false;
  if (typeof document === 'undefined') {
    return String(html).replace(/<[^>]*>/g, '').trim().length > 0;
  }
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || '').trim().length > 0;
};

const getFromSubmission = (submission) =>
  submission?.assignment?.instructions ?? submission?.instructions ?? '';

const getAssignmentId = (submission) =>
  submission?.assignment?.id ||
  submission?.assignmentId ||
  submission?.assignment?.assignmentId;

// Loads assignment instructions HTML: prefilled from the submission when
// present, otherwise fetched lazily via getAssignmentById. Shared by the
// question-navigation popup modal and the Task Details instructions section.
export const useInstructions = (submission) => {
  const isAuthenticated = isLoggedIn();
  const [html, setHtml] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const ensureLoaded = useCallback(async () => {
    const fromSubmission = getFromSubmission(submission);
    if (hasInstructionContent(fromSubmission)) {
      setHtml(fromSubmission);
      return;
    }

    const assignmentId = getAssignmentId(submission);
    // The shared submission already includes any public instructions. Its
    // assignment endpoint remains private, even when instructions are empty.
    if (!assignmentId || !isAuthenticated) return;

    setIsLoading(true);
    try {
      const assignment = await getAssignmentById(assignmentId);
      const loaded = assignment?.instructions || '';
      if (hasInstructionContent(loaded)) setHtml(loaded);
    } catch (error) {
      console.error('Failed to load instructions', error);
    } finally {
      setIsLoading(false);
    }
  }, [submission, isAuthenticated]);

  // Eagerly resolve instructions so consumers can know upfront whether any
  // exist (e.g. to decide whether to show a "View instructions" button).
  useEffect(() => {
    ensureLoaded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    submission?.assignment?.instructions,
    submission?.instructions,
    submission?.assignment?.id,
    submission?.assignmentId,
    isAuthenticated,
  ]);

  return {
    html,
    isLoading,
    ensureLoaded,
    hasInstructions: hasInstructionContent(html),
  };
};
